import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  ShieldCheck, Zap, Smartphone, Camera, User, MapPin, 
  Phone, Heart, Shield, CheckCircle2, X, PlusCircle, Globe,
  ArrowRight, CreditCard, RefreshCw, HardDrive, Key, FileText
} from 'lucide-react';

/**
 * 🛰️ SAHELGUARD PARTNER TERMINAL - V10.0 (FINAL SOUVERAIN)
 * ---------------------------------------------------
 * Architecture : Moussa Yarbanga | Yarmotek Empire
 * Fonctions : KYC Biométrique, Scellage D1, Ravitaillement Mobile Money.
 * Statut : PRODUCTION READY - SCELLÉ LE 18/03/2026
 * ---------------------------------------------------
 */

const PartnerPortal = ({ agentId, apiUrl }) => {
  const [agent, setAgent] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const videoRef = useRef(null);
  const [photo, setPhoto] = useState(null);

  // --- 📝 STRUCTURE KYC COMPLÈTE (CONFORMITÉ UEMOA) ---
  const [sale, setSale] = useState({
    firstName: '', lastName: '', gender: 'M', 
    dob: '', pob: '', // Date et Lieu de naissance
    idNumber: '', idIssueDate: '', idIssuePlace: '', // Détails de la pièce (CNI/Passport)
    phone: '', email: '', whatsapp: '',
    country: 'Burkina Faso', city: '', district: '', address: '',
    brandModel: '', imei1: '', imei2: '',
    emergency1: '', emergency2: '', emergency3: '',
    pack: 'SENTINELLE', billingCycle: 'MONTHLY', assuranceLevel: '0'
  });

  // --- 📦 CATALOGUE OFFICIEL DES PACKS SAHELGUARD ---
  const PACKS = {
    SENTINELLE: { monthly: 1000, annual: 10000, units: 1, storage: "32 Go" },
    PIVOT:      { monthly: 2000, annual: 20000, units: 2, storage: "64 Go" },
    REMPART:    { monthly: 4000, annual: 40000, units: 4, storage: "128 Go" },
    BOUCLIER:   { monthly: 7500, annual: 75000, units: 8, storage: "256 Go" },
    CITADELLE:  { monthly: 12000, annual: 120000, units: 16, storage: "512 Go" },
    FORTERESSE: { monthly: 20000, annual: 200000, units: 32, storage: "1024 Go" }
  };

  // --- 🛡️ CATALOGUE ASSURANCE ANTI-VOL (PALIERS OFFICIELS) ---
  const ASSURANCE_PALIERS = [
    { label: "Sans Assurance", monthly: 0, annual: 0, plafond: 0 },
    { label: "1 - 150.000 F", monthly: 1000, annual: 10000, plafond: 150000 },
    { label: "151.000 - 300.000 F", monthly: 2000, annual: 20000, plafond: 300000 },
    { label: "301.000 - 450.000 F", monthly: 3000, annual: 30000, plafond: 450000 },
    { label: "451.000 - 600.000 F", monthly: 4000, annual: 40000, plafond: 600000 },
    { label: "601.000 - 750.000 F", monthly: 5000, annual: 50000, plafond: 750000 },
    { label: "751.000 - 900.000 F", monthly: 6000, annual: 60000, plafond: 900000 },
    { label: "901.000 - 1.050.000 F", monthly: 7000, annual: 70000, plafond: 1050000 },
  ];

  const SCELLAGE_FIXE = 5000;

  // --- CALCULATEUR DE FACTURE TEMPS RÉEL ---
  const invoice = useMemo(() => {
    const p = PACKS[sale.pack];
    const a = ASSURANCE_PALIERS[parseInt(sale.assuranceLevel)];
    const subPrice = sale.billingCycle === 'MONTHLY' ? p.monthly : p.annual;
    const assuPrice = sale.billingCycle === 'MONTHLY' ? a.monthly : a.annual;
    return { 
      total: SCELLAGE_FIXE + subPrice + assuPrice, 
      subPrice, assuPrice, storage: p.storage, plafond: a.plafond 
    };
  }, [sale]);

  const syncStats = () => {
    fetch(`${apiUrl}/api/agent/stats?agentId=${agentId}`)
      .then(res => res.json())
      .then(setAgent)
      .catch(err => console.error("Sync Error:", err));
  };

  useEffect(() => { syncStats(); }, [agentId]);

  // --- LOGIQUE BIOMÉTRIQUE (CAMÉRA) ---
  const takePhoto = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      videoRef.current.srcObject = stream;
    } catch (err) {
      alert("Erreur Caméra: Veuillez autoriser l'accès.");
    }
  };

  const capture = () => {
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
    setPhoto(canvas.toDataURL('image/jpeg'));
    if (videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
    }
  };

  // --- SOUMISSION DU CONTRAT ---
  const handleKycSubmit = async (e) => {
    e.preventDefault();
    if (!agent || agent.balance < invoice.total) return alert("🛑 SOLDE BOUTIQUE INSUFFISANT. VEUILLEZ RAVITAILLER.");
    if (!photo) return alert("📸 PHOTO DU TITULAIRE REQUISE POUR LE SCELLAGE.");
    
    setIsSubmitting(true);
    try {
      const res = await fetch(`${apiUrl}/api/clients/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...sale, photoSignature: photo, invoice, agentId })
      });
      if (res.ok) { 
        const result = await res.json();
        alert(`✅ APPAREIL SCELLÉ ! ID GÉNÉRÉ : ${result.sgId}`);
        window.location.reload(); 
      }
    } catch (e) { 
      console.error(e); 
      alert("ERREUR LORS DU SCELLAGE."); 
    } finally { 
      setIsSubmitting(false); 
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans selection:bg-emerald-500/30">
      
      {/* --- TOP BAR TACTIQUE --- */}
      <div className="sticky top-0 z-[100] bg-black/80 backdrop-blur-xl border-b border-white/5 p-4 md:p-6 text-left">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-emerald-500 rounded-xl shadow-[0_0_20px_rgba(16,185,129,0.4)]">
              <ShieldCheck className="text-black" size={24} />
            </div>
            <div>
              <h1 className="text-lg font-black uppercase tracking-tighter italic leading-none">Terminal <span className="text-emerald-500">Boutique</span></h1>
              <p className="text-[8px] font-bold text-white/40 uppercase tracking-[0.2em] mt-1">{agent?.biz_name || 'SAHELGUARD PARTNER'}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 bg-white/5 p-2 rounded-[2rem] border border-white/10 px-6">
            <div className="text-right border-r border-white/10 pr-6">
              <p className="text-[8px] font-black text-emerald-500 uppercase tracking-widest mb-1">Stock Virtuel</p>
              <p className="text-2xl font-black italic">{(agent?.balance || 0).toLocaleString()} <span className="text-xs opacity-30">F</span></p>
            </div>
            <button onClick={() => setShowRechargeModal(true)} className="flex items-center gap-2 bg-emerald-500 text-black px-5 py-2.5 rounded-full text-[10px] font-black uppercase hover:bg-white transition-all shadow-lg">
              <PlusCircle size={14}/> Ravitailler
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto p-4 md:p-10 pb-40 text-left">
        <form onSubmit={handleKycSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          <div className="lg:col-span-8 space-y-8">
            
            {/* --- KYC SECTION 1: IDENTITÉ --- */}
            <Section title="État Civil & Pièce d'Identité" icon={<User size={18}/>}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input label="Prénom" placeholder="Issa" value={sale.firstName} onChange={e => setSale({...sale, firstName: e.target.value})} />
                <Input label="Nom" placeholder="OUEDRAOGO" value={sale.lastName} onChange={e => setSale({...sale, lastName: e.target.value})} />
                <Input label="Date de Naissance" type="date" value={sale.dob} onChange={e => setSale({...sale, dob: e.target.value})} />
                <Input label="Lieu de Naissance" placeholder="Ex: Ouagadougou" value={sale.pob} onChange={e => setSale({...sale, pob: e.target.value})} />
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t border-white/5">
                  <Input label="N° CNIB / Passeport" placeholder="B1234567" value={sale.idNumber} onChange={e => setSale({...sale, idNumber: e.target.value})} />
                  <Input label="Délivrée le" type="date" value={sale.idIssueDate} onChange={e => setSale({...sale, idIssueDate: e.target.value})} />
                  <Input label="Lieu de délivrance" placeholder="Ouagadougou" value={sale.idIssuePlace} onChange={e => setSale({...sale, idIssuePlace: e.target.value})} />
                </div>
              </div>
            </Section>

            {/* --- KYC SECTION 2: ADRESSE --- */}
            <Section title="Résidence & Contact" icon={<MapPin size={18}/>}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Select 
                  label="Pays (Espace Sahel)" 
                  value={sale.country} 
                  options={[
                    {v: 'Burkina Faso', l: 'Burkina Faso'}, {v: 'Mali', l: 'Mali'},
                    {v: 'Niger', l: 'Niger'}, {v: 'Sénégal', l: 'Sénégal'},
                    {v: 'Côte d\'Ivoire', l: 'Côte d\'Ivoire'}, {v: 'Bénin', l: 'Bénin'}, {v: 'Togo', l: 'Togo'}
                  ]} 
                  onChange={v => setSale({...sale, country: v})} 
                />
                <Input label="Ville" placeholder="Ex: Bobo-Dioulasso" value={sale.city} onChange={e => setSale({...sale, city: e.target.value})} />
                <Input label="Quartier / Secteur" placeholder="Ex: Secteur 15" value={sale.district} onChange={e => setSale({...sale, district: e.target.value})} />
                <Input label="Adresse Complète" placeholder="Rue 15.22, Porte 40" value={sale.address} onChange={e => setSale({...sale, address: e.target.value})} />
                <Input label="Téléphone (WhatsApp)" placeholder="+226 70 00 00 00" value={sale.whatsapp} onChange={e => setSale({...sale, whatsapp: e.target.value, phone: e.target.value})} />
                <Input label="Email" type="email" placeholder="client@sahelguard.com" value={sale.email} onChange={e => setSale({...sale, email: e.target.value})} />
              </div>
            </Section>

            {/* --- KYC SECTION 3: SÉCURITÉ --- */}
            <Section title="Protocole Alerte (Numéros de Secours)" icon={<Heart size={18}/>} highlight>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Input label="N° de Secours 1" placeholder="70 00 00 00" value={sale.emergency1} onChange={e => setSale({...sale, emergency1: e.target.value})} />
                <Input label="N° de Secours 2" placeholder="75 00 00 00" value={sale.emergency2} onChange={e => setSale({...sale, emergency2: e.target.value})} />
                <Input label="N° de Secours 3" placeholder="78 00 00 00" value={sale.emergency3} onChange={e => setSale({...sale, emergency3: e.target.value})} />
              </div>
            </Section>

            {/* --- KYC SECTION 4: APPAREIL --- */}
            <Section title="Spécifications Technique" icon={<Smartphone size={18}/>}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Input label="Marque & Modèle" placeholder="Samsung S23 Ultra" value={sale.brandModel} onChange={e => setSale({...sale, brandModel: e.target.value})} />
                <Input label="IMEI 1" placeholder="Obligatoire" value={sale.imei1} onChange={e => setSale({...sale, imei1: e.target.value})} />
                <Input label="IMEI 2" placeholder="Optionnel" value={sale.imei2} onChange={e => setSale({...sale, imei2: e.target.value})} />
              </div>
            </Section>

            {/* --- KYC SECTION 5: BIOMÉTRIE --- */}
            <Section title="Signature Visuelle" icon={<Camera size={18}/>}>
              <div className="flex flex-col md:flex-row gap-8 items-center bg-black/40 p-6 rounded-[2rem] border border-white/5">
                <div className="w-full md:w-72 aspect-[4/3] bg-black rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
                  {!photo ? <video ref={videoRef} autoPlay className="w-full h-full object-cover grayscale" /> : <img src={photo} className="w-full h-full object-cover" alt="Capture KYC" />}
                </div>
                <div className="space-y-4 flex-1 text-left">
                  <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider italic leading-relaxed">
                    Capture biographique du titulaire avec son appareil pour validation juridique du scellage.
                  </p>
                  <div className="flex gap-3">
                    {!photo ? (
                      <button type="button" onClick={takePhoto} className="px-6 py-3 bg-white/5 border border-white/10 rounded-full text-[10px] font-black uppercase">Activer Caméra</button>
                    ) : (
                      <button type="button" onClick={() => setPhoto(null)} className="px-6 py-3 bg-red-500/20 text-red-500 rounded-full text-[10px] font-black uppercase">Refaire</button>
                    )}
                    {videoRef.current?.srcObject && !photo && (
                      <button type="button" onClick={capture} className="px-8 py-3 bg-emerald-500 text-black rounded-full text-[10px] font-black uppercase shadow-lg">CAPTURER LE SCEAU</button>
                    )}
                  </div>
                </div>
              </div>
            </Section>
          </div>

          {/* --- SIDEBAR FACTURATION --- */}
          <div className="lg:col-span-4">
            <div className="sticky top-32 space-y-6">
              <div className="bg-emerald-500 p-8 rounded-[3.5rem] text-black shadow-2xl relative overflow-hidden">
                <Shield className="absolute -right-4 -top-4 opacity-10" size={150} />
                <h3 className="text-xs font-black uppercase tracking-widest border-b border-black/10 pb-4 mb-8 italic">Récapitulatif Vente</h3>
                <div className="space-y-4 mb-10">
                  <Row label="Droit de Sceau Fixe" val="5 000 F" />
                  <Row label={`Pack ${sale.pack}`} val={`${invoice.subPrice.toLocaleString()} F`} />
                  <Row label="Couverture Assurance" val={`${invoice.assuPrice.toLocaleString()} F`} />
                  <div className="pt-6 border-t border-black/20 text-right">
                    <p className="text-[10px] font-black uppercase opacity-60 mb-1">Net à Payer (Stock)</p>
                    <p className="text-6xl font-black italic tracking-tighter leading-none">{invoice.total.toLocaleString()} <span className="text-sm not-italic">F</span></p>
                  </div>
                </div>
                <div className="space-y-4 mb-10 text-left">
                  <Select label="Durée" value={sale.billingCycle} options={[{v:'MONTHLY', l:'Mensuel'}, {v:'ANNUAL', l:'Annuel (-2 mois)'}]} onChange={v => setSale({...sale, billingCycle: v})} />
                  <Select label="Pack Cloud R2" value={sale.pack} options={Object.keys(PACKS).map(p=>({v:p, l:`${p} (${PACKS[p].storage})`}))} onChange={v => setSale({...sale, pack: v})} />
                  <Select label="Assurance Anti-Vol" value={sale.assuranceLevel} options={ASSURANCE_PALIERS.map((a,i)=>({v:i, l:a.label}))} onChange={v => setSale({...sale, assuranceLevel: v})} />
                </div>
                <button type="submit" disabled={isSubmitting} className="w-full bg-black text-white py-8 rounded-[2.5rem] font-black uppercase italic tracking-[0.2em] text-xs hover:scale-105 transition-all shadow-2xl disabled:opacity-50">
                  {isSubmitting ? <RefreshCw className="animate-spin mx-auto" /> : "GÉNÉRER LE CONTRAT SCELLÉ"}
                </button>
              </div>
            </div>
          </div>
        </form>
      </main>

      {/* --- MODAL RECHARGE --- */}
      {showRechargeModal && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl">
          <div className="bg-[#0a0a0a] border border-emerald-500/20 w-full max-w-md rounded-[3rem] p-10 relative shadow-2xl">
            <button onClick={() => setShowRechargeModal(false)} className="absolute top-8 right-8 text-white/40 hover:text-white transition-all"><X size={32}/></button>
            <Zap className="text-emerald-500 mx-auto mb-6" size={48} />
            <h3 className="text-3xl font-black uppercase italic text-center mb-8">Ravitailler Stock</h3>
            <RechargeModule agentId={agentId} apiUrl={apiUrl} onDone={() => { syncStats(); setShowRechargeModal(false); }} />
          </div>
        </div>
      )}
    </div>
  );
};

// --- MODULE PAIEMENT ---
const RechargeModule = ({ agentId, apiUrl, onDone }) => {
  const [step, setStep] = useState('amount');
  const [amount, setAmount] = useState('');
  const [phone, setPhone] = useState('');
  const [network, setNetwork] = useState('ORANGE');
  const [otp, setOtp] = useState('');

  const startPayment = async () => {
    setStep('processing');
    try {
      const res = await fetch(`${apiUrl}/api/wallet/deposit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentId, amount: Number(amount), phone, network, otp })
      });
      if (res.ok) { 
        setStep('success'); 
        setTimeout(onDone, 2000); 
      } else { 
        alert("Échec du paiement."); 
        setStep('amount'); 
      }
    } catch (e) { 
      alert("Erreur Réseau."); 
      setStep('amount'); 
    }
  };

  return (
    <div className="space-y-6">
      {step === 'amount' && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <button onClick={() => setNetwork('ORANGE')} className={`p-4 rounded-2xl border-2 font-black text-[10px] ${network === 'ORANGE' ? 'border-orange-500 bg-orange-500/10 text-orange-500' : 'border-white/5 opacity-40'}`}>Orange Money</button>
            <button onClick={() => setNetwork('MOOV')} className={`p-4 rounded-2xl border-2 font-black text-[10px] ${network === 'MOOV' ? 'border-blue-500 bg-blue-500/10 text-blue-500' : 'border-white/5 opacity-40'}`}>Moov Money</button>
          </div>
          <input type="number" placeholder="MONTANT (FCFA)" className="w-full bg-white/5 border border-white/10 p-6 rounded-2xl text-2xl font-black text-center text-white outline-none" value={amount} onChange={e => setAmount(e.target.value)} />
          <input type="tel" placeholder="N° DE TÉLÉPHONE" className="w-full bg-white/5 border border-white/10 p-6 rounded-2xl text-2xl font-black text-center text-white outline-none" value={phone} onChange={e => setPhone(e.target.value)} />
          {network === 'ORANGE' && <input type="text" placeholder="CODE OTP (*144*4*6#)" className="w-full bg-white/5 border border-orange-500/30 p-6 rounded-2xl text-2xl font-black text-center text-orange-500 outline-none" value={otp} onChange={e => setOtp(e.target.value)} maxLength={6} />}
          <button onClick={startPayment} className="w-full bg-emerald-500 text-black font-black py-7 rounded-3xl uppercase text-xs shadow-xl active:scale-95 transition-all">Payer Maintenant</button>
        </>
      )}
      {step === 'processing' && <div className="py-20 text-center"><RefreshCw className="animate-spin mx-auto text-emerald-500" size={48} /><p className="mt-4 font-black uppercase text-xs italic">Transaction en cours...</p></div>}
      {step === 'success' && <div className="py-20 text-center text-emerald-500"><CheckCircle2 className="mx-auto" size={64} /><p className="mt-4 font-black uppercase text-xs italic">Stock Ravitaillé !</p></div>}
    </div>
  );
};

// --- COMPOSANTS ATOMIQUES ---
const Section = ({ title, icon, children, highlight }) => (
  <div className={`p-8 md:p-10 rounded-[3.5rem] border ${highlight ? 'border-emerald-500/30 bg-emerald-500/5 shadow-2xl' : 'border-white/5 bg-[#0a0a0a]'} mb-8 shadow-xl text-left`}>
    <h3 className="text-[11px] font-black uppercase mb-10 flex items-center gap-4 tracking-[0.2em] text-emerald-500">
      <div className="p-2 bg-emerald-500/10 rounded-lg">{icon}</div> {title}
    </h3>
    {children}
  </div>
);

const Input = ({ label, type = "text", ...props }) => (
  <div className="space-y-2 text-left">
    <label className="text-[10px] font-black text-slate-500 uppercase ml-2 tracking-widest">{label}</label>
    <input type={type} {...props} required className="w-full bg-white/5 border border-white/10 p-5 rounded-2xl font-bold text-sm outline-none focus:border-emerald-500 transition-all text-white" />
  </div>
);

const Select = ({ label, options, value, onChange }) => (
  <div className="space-y-2 text-left">
    <label className="text-[9px] font-black text-slate-500 uppercase ml-4">{label}</label>
    <select value={value} onChange={e => onChange(e.target.value)} className="w-full bg-black/40 border border-white/10 p-4 rounded-2xl text-xs font-black uppercase outline-none text-white">
      {options.map(o => <option key={o.v} value={o.v} className="bg-[#050505]">{o.l}</option>)}
    </select>
  </div>
);

const Row = ({ label, val }) => (
  <div className="flex justify-between items-center text-xs font-black uppercase tracking-tighter mb-2">
    <span className="opacity-60">{label}</span>
    <span>{val}</span>
  </div>
);

export default PartnerPortal;